import React, { useContext, useMemo } from 'react';
import { LogViewer } from '../log-viewer';
import { LogType } from '../../../types/visualizer';
import { AuthContext } from '@@/contexts/AuthContext';
import { useConfigurationContext } from '../../../contexts/configuration-context';
import { transformSpansToLogItems } from './transform-spans';
import { Spinner } from '@@/ui/components';
import { Contact } from '@aws-sdk/client-connect';
import { useWisdomSpans } from '../../../hooks/use-wisdom-spans';

interface ToolCitationsViewerProps {
  contact: Contact;
}

export const ToolCitationsViewer = (props: ToolCitationsViewerProps) => {
  const { instanceData } = useContext(AuthContext);
  const configuration = useConfigurationContext();

  // Extract assistantId and sessionId from contact
  const sessionArnParts = props.contact.WisdomInfo?.SessionArn?.split('/') || [];
  const assistantId = sessionArnParts[1];
  const sessionId = sessionArnParts[2];

  const { data, isLoading, error } = useWisdomSpans({
    assistantId: assistantId!,
    sessionId: sessionId!
  });

  // Transform spans to log items
  const logItems = useMemo(() => {
    if (!instanceData || !data?.spans) return [];

    return transformSpansToLogItems(data.spans, configuration.connect.instanceAlias, instanceData.awsAccountId);
  }, [data?.spans, instanceData, configuration]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>
        Error loading Tool & Citations: {error.message}
      </div>
    );
  }

  if (!data?.spans || data.spans.length === 0) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>No Tool & Citations data available</div>
    );
  }

  return (
    <LogViewer logs={logItems} logType={LogType.TOOL_CITATIONS_LOG} emptyMessage='No Tool & Citations available' />
  );
};
